import { FC, useMemo } from 'react';
import { Typography } from '@mui/material';
import { useStore } from '../../../hooks/useStore';
import { shallow } from 'zustand/shallow';
import { AdElementText } from '@adflow/types';
import { Flex, Box } from '@chakra-ui/react';
import {
  buildAvailableFonts,
  extractStretchFromFamily,
  getVariantLabel,
  normalizeVariantLabel,
} from './utils/fontUtils';
import { FontFamilySelect } from './components/FontFamilySelect';
import { FontVariantSelect } from './components/FontVariantSelect';

type Props = {
  selectedElement: AdElementText;
};

const FontSelectionControls: FC<Props> = ({ selectedElement }) => {
  const store = useStore(
    (state) => ({
      updateElementData: state.updateElementData,
      customFonts: state.customFonts,
      standardFonts: state.standardFonts,
    }),
    shallow,
  );

  const currentFamily = selectedElement.data.style.fontFamily || '';
  const currentVariantLabel = selectedElement.data.style.fontVariant || '';
  const currentWeight = selectedElement.data.style.fontWeight || 400;
  const currentStyle = selectedElement.data.style.fontStyle || 'normal';
  const currentStretch = selectedElement.data.style.fontStretch || 'normal';

  const availableFontsGrouped = useMemo(() => {
    return buildAvailableFonts({
      customFonts: store.customFonts,
      standardFonts: store.standardFonts,
      originalFamily: currentFamily,
      originalStyle: currentStyle,
      originalWeight: currentWeight,
      originalStretch: currentStretch,
    });
  }, [
    store.customFonts,
    store.standardFonts,
    currentFamily,
    currentStyle,
    currentWeight,
    currentStretch,
  ]);

  // Find the currently selected font group
  const selectedFontGroup = useMemo(() => {
    return availableFontsGrouped.find((f) => f.family === currentFamily);
  }, [availableFontsGrouped, currentFamily]);

  const normalizedCustomVariants = useMemo(() => {
    const { baseFamily } = extractStretchFromFamily(currentFamily);
    return store.customFonts
      .map((font) => {
        const { baseFamily: fontBaseFamily, stretch: fontFamilyStretch } = extractStretchFromFamily(
          font.fontFamily,
        );
        if (fontBaseFamily !== baseFamily) return null;

        const fontWeight = font.weight ?? 400;
        const fontStyle = font.style && font.style.trim() !== '' ? font.style : 'normal';
        const fontStretch = font.stretch && font.stretch.trim() !== '' ? font.stretch : 'normal';
        const finalFontStretch = fontStretch !== 'normal' ? fontStretch : fontFamilyStretch;

        return {
          weight: fontWeight,
          style: fontStyle,
          stretch: finalFontStretch,
        };
      })
      .filter(Boolean) as Array<{ weight: number; style: string; stretch: string }>;
  }, [store.customFonts, currentFamily]);

  const normalizedCustomVariantLabels = useMemo(() => {
    // For custom fonts, use exact family name without normalization
    return new Set(
      store.customFonts
        .map((font) => {
          // Match by exact family name
          if (font.fontFamily !== currentFamily) return null;

          const fontWeight = font.weight ?? 400;
          const fontStyle = font.style && font.style.trim() !== '' ? font.style : 'normal';
          const label = normalizeVariantLabel(getVariantLabel(fontWeight, fontStyle, font.variant));

          return label || null;
        })
        .filter(Boolean) as string[],
    );
  }, [store.customFonts, currentFamily]);

  const isCustomFamily = useMemo(
    () => normalizedCustomVariants.length > 0,
    [normalizedCustomVariants.length],
  );

  const isMissingVariant = useMemo(() => {
    if (!currentFamily || !isCustomFamily) return false;

    const normalizedLabel = normalizeVariantLabel(currentVariantLabel);
    if (normalizedLabel) {
      return !normalizedCustomVariantLabels.has(normalizedLabel);
    }

    const normalizedWeight = currentWeight || 400;
    const normalizedStyle = currentStyle || 'normal';
    const normalizedStretch = currentStretch || 'normal';
    const { stretch: familyStretch } = extractStretchFromFamily(currentFamily);
    const finalStretch = normalizedStretch !== 'normal' ? normalizedStretch : familyStretch;

    const exactMatch = normalizedCustomVariants.some(
      (variant) =>
        variant.weight === normalizedWeight &&
        variant.style === normalizedStyle &&
        variant.stretch === finalStretch,
    );

    if (exactMatch) return false;

    const ignoreStretchMatches = normalizedCustomVariants.filter(
      (variant) => variant.weight === normalizedWeight && variant.style === normalizedStyle,
    );

    return ignoreStretchMatches.length !== 1;
  }, [
    currentFamily,
    currentVariantLabel,
    currentWeight,
    currentStyle,
    currentStretch,
    isCustomFamily,
    normalizedCustomVariants,
    normalizedCustomVariantLabels,
  ]);

  const missingVariantLabel = useMemo(() => {
    if (!isMissingVariant) return '';
    const normalizedLabel = normalizeVariantLabel(currentVariantLabel);
    if (normalizedLabel) return currentVariantLabel.trim();
    const normalizedWeight = currentWeight || 400;
    const normalizedStyle = currentStyle || 'normal';
    return getVariantLabel(normalizedWeight, normalizedStyle);
  }, [currentVariantLabel, currentWeight, currentStyle, isMissingVariant]);

  const handleFamilyChange = (family: string) => {
    // Find the new family group
    const newGroup = availableFontsGrouped.find((f) => f.family === family);

    // Default to Regular (400, normal, normal stretch) or the first available variant
    let newWeight = 400;
    let newStyle = 'normal';
    let newStretch = 'normal';
    let newVariantLabel = getVariantLabel(newWeight, newStyle);

    if (newGroup && newGroup.stretchGroups.length > 0) {
      // Try to find normal stretch group first
      const normalStretchGroup = newGroup.stretchGroups.find((sg) => sg.stretch === 'normal');
      const targetGroup = normalStretchGroup || newGroup.stretchGroups[0];

      // Try to find Regular/Normal variant
      const regular = targetGroup.variants.find((v) => v.weight === 400 && v.style === 'normal');
      if (regular) {
        newWeight = regular.weight;
        newStyle = regular.style;
        newStretch = regular.stretch;
        newVariantLabel = regular.label;
      } else if (targetGroup.variants.length > 0) {
        // Fallback to the first variant in the target group
        const fallbackVariant = targetGroup.variants[0];
        newWeight = fallbackVariant.weight;
        newStyle = fallbackVariant.style;
        newStretch = fallbackVariant.stretch;
        newVariantLabel = fallbackVariant.label;
      }
    }

    store.updateElementData(selectedElement.id, {
      ...selectedElement.data,
      style: {
        ...selectedElement.data.style,
        fontFamily: family,
        fontWeight: newWeight,
        fontStyle: newStyle,
        fontStretch: newStretch,
        fontVariant: newVariantLabel,
      },
    });
  };

  const handleVariantChange = (next: {
    weight: number;
    style: string;
    stretch: string;
    label: string;
  }) => {
    const weight = next.weight;
    const style = next.style;
    const stretch = next.stretch && next.stretch.trim() !== '' ? next.stretch : 'normal';
    const label =
      next.label && next.label.trim() !== '' ? next.label : getVariantLabel(weight, style);

    store.updateElementData(selectedElement.id, {
      ...selectedElement.data,
      style: {
        ...selectedElement.data.style,
        fontWeight: weight,
        fontStyle: style,
        fontStretch: stretch,
        fontVariant: label,
      },
    });
  };

  return (
    <Box mt={5}>
      <Typography variant="h4" fontWeight="bold">
        Font
      </Typography>
      <Flex direction="column" gap={2} mt={2}>
        <FontFamilySelect
          value={currentFamily}
          fonts={availableFontsGrouped}
          standardFonts={store.standardFonts}
          customFonts={store.customFonts}
          onChange={handleFamilyChange}
        />

        <FontVariantSelect
          currentWeight={currentWeight}
          currentStyle={currentStyle}
          currentStretch={currentStretch}
          fontGroup={selectedFontGroup}
          missingVariant={isMissingVariant}
          missingVariantLabel={missingVariantLabel}
          disabled={!currentFamily}
          onChange={handleVariantChange}
        />
      </Flex>
    </Box>
  );
};

FontSelectionControls.displayName = 'FontSelectionControls';

export default FontSelectionControls;
export type { Props as FontSelectionControlsProps };
